import React, { FunctionComponent } from "react";
import styles from "./TextImageBanner.module.scss";
import TextImageBannerBlockProps from "./TextImageBannerProps.interface";

import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";
import BoxWrap from "components/BoxWrap/BoxWrap";
import Button from "components/Button/Button";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import ImageViewer from "components/ImageViewer/ImageViewer";
import Paragraph from "components/Paragraph/Paragraph";
import setInViewClasses from "blocks/hooks/setInViewClasses";
import useAnchor from "blocks/hooks/useAnchor";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";

const TextImageBanner: FunctionComponent<TextImageBannerBlockProps> = (props): JSX.Element => {
    const wrapperClasses = getWrapperClasses(props.settings);
    const wrapperStyles = useWrapperStyles(props.settings);
    const { inViewElementRef, inViewClasses } = setInViewClasses(styles);

    const identifier = useAnchor(props.settings.anchorMenuLabel);
    const { splitClasses, splitStyles } = useSplitBackground(props);

    const bannerSizeClass = props.settings.bannerSize ? styles[`size_${props.settings.bannerSize}`] : styles.size_Medium;
    const headlineCaseClass = props.settings.headlineCase ? styles[`case_${props.settings.headlineCase}`] : undefined;

    const imagePositionClass = props.settings.imagePosition === "Media Right" ? styles.has_image_right : undefined;
    const imageOverflowClass = props.settings.imageOverflow === true ? styles.has_overflow : styles.has_split;

    return (
        <section className={combineClasses(styles.wrapper, ...wrapperClasses)} style={{ ...wrapperStyles, ...splitStyles }} id={identifier}>
            <div className={combineClasses(styles.container, ...splitClasses, imageOverflowClass, bannerSizeClass, imagePositionClass, inViewClasses)} ref={inViewElementRef}>
                <div className={styles.media}>
                    <div className={styles.media_inner}>
                        {/* Image */}
                        {props.content.image ? (
                            <ImageViewer key={`img_${props.contentUdi}`} className={styles.image} src={props.content.image.umbracoFile.src} extensionType={props.content.image.umbracoExtension} type="cover" focalPoint={props.content.image.umbracoFile.focalPoint} alt={props.content.imageAlternative} />
                        ) : null}
                    </div>
                </div>

                <div className={styles.content}>
                    <div className={styles.content_inner}>
                        {/* TODO: Refactor BoxWrap Component to useBoxWrap hook */}
                        <BoxWrap {...props?.settings?.boxWrap && props.settings.boxWrap?.[0]} className={combineClasses(styles.inner, inViewClasses)} ref={inViewElementRef}>
                            {/* Tagline */}
                            {props.content.tagline ? (
                                <span className={styles.tagline}>{props.content.tagline}</span>
                            ) : null}

                            {/* Headline */}
                            {props.content.headline ? (
                                <DynamicHeading key={`head_${props.contentUdi}`} className={combineClasses(styles.headline, headlineCaseClass)} text={props.content.headline} headlineSize={props.settings.headlineSize} headlineWeight={props.settings.headlineWeight} />
                            ) : null}

                            {/* Text */}
                            {props.content.bodyText ? (
                                <Paragraph key={`para_${props.contentUdi}`} className={styles.text} innerHTML={props.content.bodyText} />
                            ) : null}

                            {/* Buttons */}
                            {props.content.buttons && props.content.buttons.length > 0 ? (
                                <div className={styles.buttons}>
                                    {props.content.buttons.map((button) => (
                                        <Button key={button.contentUdi} to={button.content.link} className={styles.button} variant={button.content.variant} {...button.content}>{button.content.label}</Button>
                                    ))}
                                </div>
                            ) : null}
                        </BoxWrap>
                    </div>
                </div>
            </div>

            {props.settings.backgroundMedia?.[0] && props.settings.backgroundMedia.length > 0 ? (
                <BackgroundMedia key={`${props.contentUdi}`} {...props.settings.backgroundMedia[0]} />
            ) : null}
        </section>
    );
};

export default TextImageBanner;